import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';



// Define all available permissions
const ALL_PERMISSIONS = [
  { id: "manage_users", label: "Manage Users", description: "Create, edit, and delete users" },
  { id: "manage_security", label: "Manage Security", description: "Manage security settings and IP blacklisting" },
  { id: "manage_products", label: "Manage Products", description: "Create, edit, and delete products" },
  { id: "generate_manual_quote", label: "Generate Manual Quotes", description: "Create quotes with custom items" },
  { id: "generate_individual_item_quote", label: "Individual Item Quote Generator", description: "Access to the individual item quote form" },
  { id: "approve_quotes", label: "Approve Quotes", description: "Approve or reject quotes" },
  { id: "manage_quotes", label: "Manage Quotes", description: "View and manage all quotes" },
  { id: "edit_quote_fields", label: "Edit Quote Fields", description: "Edit quote terms and conditions" },
  { id: "manage_letterheads", label: "Manage Letterheads", description: "Upload and manage letterheads" },
  { id: "manage_content", label: "Manage Content", description: "Manage website content" },
  { id: "send_quotes", label: "Send Quotes", description: "Send quotes to clients via email" },
  { id: "manage_settings", label: "Manage Settings", description: "Change system settings" }
];

function RoleManagement() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState({ 
    name: '', 
    permissions: [],
    isCreating: false
  });
  const [editingRole, setEditingRole] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get(`/roles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Filter out the superadmin role from the list
      const filteredRoles = response.data.filter(role => role.role_id !== 'superadmin');
      setRoles(filteredRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setMessage({ type: 'error', text: 'Failed to fetch roles' });
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    if (editingRole) {
      // Editing existing role
      const updatedPermissions = checked 
        ? [...editingRole.permissions, permissionId]
        : editingRole.permissions.filter(p => p !== permissionId);
      
      setEditingRole({ ...editingRole, permissions: updatedPermissions });
    } else {
      // Creating new role
      const updatedPermissions = checked 
        ? [...newRole.permissions, permissionId]
        : newRole.permissions.filter(p => p !== permissionId);
      
      setNewRole({ ...newRole, permissions: updatedPermissions });
    }
  };

  const createRole = async () => {
    if (!newRole.name.trim()) {
      setMessage({ type: 'error', text: 'Role name is required' });
      return;
    }
    
    if (newRole.permissions.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one permission' });
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/roles`, {
        name: newRole.name,
        permissions: newRole.permissions
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setMessage({ type: 'success', text: 'Role created successfully' });
      setNewRole({ name: '', permissions: [], isCreating: false });
      fetchRoles();
    } catch (error) {
      console.error('Error creating role:', error);
      setMessage({ type: 'error', text: 'Failed to create role' });
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async () => {
    if (!editingRole) return;
    
    setLoading(true);
    try {
      await apiClient.put(`/roles/${editingRole.role_id}`, {
        permissions: editingRole.permissions
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setMessage({ type: 'success', text: 'Role updated successfully' });
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      setMessage({ type: 'error', text: 'Failed to update role' });
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role? Users with this role will be assigned the default moderator role.')) {
      return;
    }
    
    try {
      await apiClient.delete(`/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setMessage({ type: 'success', text: 'Role deleted successfully' });
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      setMessage({ type: 'error', text: 'Failed to delete role' });
    }
  };

  const startEditing = (role) => {
    setEditingRole({ ...role });
    setNewRole({ name: '', permissions: [], isCreating: false });
  };

  const cancelEditing = () => {
    setEditingRole(null);
  };

  const startCreating = () => {
    setNewRole({ name: '', permissions: [], isCreating: true });
    setEditingRole(null);
  };

  const cancelCreating = () => {
    setNewRole({ name: '', permissions: [], isCreating: false });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Role Management</CardTitle>
          {user?.role === 'superadmin' && (
            <Button onClick={startCreating} disabled={newRole.isCreating || editingRole}>
              <Plus className="mr-2" size={16} /> Create New Role
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {message.text && (
          <Alert className={`mb-4 ${message.type === 'error' ? 'border-red-500' : 'border-green-500'}`}>
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {/* Create New Role Form */}
        {newRole.isCreating && (
          <Card className="mb-6 border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4">Create New Role</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="roleName">Role Name</Label>
                  <Input
                    id="roleName"
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    placeholder="Enter role name"
                  />
                </div>
              </div>
              
              <h4 className="font-medium mb-2">Permissions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {ALL_PERMISSIONS.map(permission => (
                  <div key={permission.id} className="flex items-center space-x-2 p-2 border rounded">
                    <Checkbox
                      id={`create-${permission.id}`}
                      checked={newRole.permissions.includes(permission.id)}
                      onCheckedChange={(checked) => 
                        handlePermissionChange(permission.id, checked)
                      }
                    />
                    <label htmlFor={`create-${permission.id}`} className="text-sm">
                      <div className="font-medium">{permission.label}</div>
                      <div className="text-xs text-gray-500">{permission.description}</div>
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <Button onClick={createRole} disabled={loading}>
                  <Save className="mr-2" size={16} /> Create Role
                </Button>
                <Button variant="outline" onClick={cancelCreating}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Role Form */}
        {editingRole && (
          <Card className="mb-6 border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4">Edit Role: {editingRole.name}</h3>
              
              <h4 className="font-medium mb-2">Permissions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {ALL_PERMISSIONS.map(permission => (
                  <div key={permission.id} className="flex items-center space-x-2 p-2 border rounded">
                    <Checkbox
                      id={`edit-${permission.id}`}
                      checked={editingRole.permissions.includes(permission.id)}
                      onCheckedChange={(checked) => 
                        handlePermissionChange(permission.id, checked)
                      }
                    />
                    <label htmlFor={`edit-${permission.id}`} className="text-sm">
                      <div className="font-medium">{permission.label}</div>
                      <div className="text-xs text-gray-500">{permission.description}</div>
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <Button onClick={updateRole} disabled={loading}>
                  <Save className="mr-2" size={16} /> Update Role
                </Button>
                <Button variant="outline" onClick={cancelEditing}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Roles List */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role Name</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map(role => (
              <TableRow key={role.role_id}>
                <TableCell className="font-medium">{role.name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.map(permission => {
                      const permInfo = ALL_PERMISSIONS.find(p => p.id === permission);
                      return (
                        <Badge key={permission} variant="outline" className="text-xs">
                          {permInfo ? permInfo.label : permission}
                        </Badge>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => startEditing(role)}
                    >
                      Edit
                    </Button>
                    {user?.role === 'superadmin' && (
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => deleteRole(role.role_id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default RoleManagement;